import { type Form } from '@wix/ambassador-forms-v4-form/types';
import {
  type ExternalData,
  type AddressTemplate,
  type FormValues,
  type PossibleValues,
} from '@wix/form-fields';
import { type ValidationError, type ValidationMode } from '@wix/form-validator';

import { runAdditionalValidation } from './additional-field-validation';
import { validateFields } from './validation';

export const getFieldErrors = async (
  fieldsToValidate: { path: string; value: PossibleValues }[],
  form: Form,
  addressTemplates?: AddressTemplate[],
  externalData?: ExternalData,
  validationMode?: ValidationMode,
) => {
  const formValues: FormValues = fieldsToValidate.reduce(
    (acc, field) => setValue(acc, field.path, field.value),
    {},
  );

  const result = await validateFields(
    form,
    formValues,
    addressTemplates,
    validationMode,
  );
  const _errors = result?.errors || [];

  const additionalErrors = runAdditionalValidation({
    form,
    values: formValues,
    externalData,
  });

  const allErrors = mergeErrors({ errors: _errors, additionalErrors });

  const fieldErrors = allErrors.filter((error) =>
    findErrorByNormalizedPath(fieldsToValidate, error.errorPath),
  );

  return fieldErrors;
};

export const findErrorByNormalizedPath = (
  fieldsToValidate: { path: string; value: PossibleValues }[],
  errorPath: string,
) => {
  return !!fieldsToValidate.find(
    (field) =>
      errorPath === field.path || errorPath.startsWith(`${field.path}/`),
  );
};

function setValue(object: any, path: string, value: any) {
  const delimiter = '/';
  const decomposedPath = path.split(delimiter);
  const base = decomposedPath[0];

  if (base === undefined) {
    return object;
  }

  if (!object.hasOwnProperty(base)) {
    object[base] = {};
  }

  value =
    decomposedPath.length <= 1
      ? value
      : setValue(object[base], decomposedPath.slice(1).join(delimiter), value);

  return {
    ...object,
    [base]: value,
  };
}

export const mergeErrors = ({
  errors,
  additionalErrors,
}: {
  errors: ValidationError[];
  additionalErrors?: Record<string, ValidationError>;
}) => {
  const errorsToAdd: ValidationError[] = [];
  if (additionalErrors) {
    Object.keys(additionalErrors).forEach((fieldTarget) => {
      const errorExists = errors.find(
        (error) => error.errorPath === fieldTarget,
      );
      if (!errorExists) {
        errorsToAdd.push(additionalErrors[fieldTarget]);
      }
    });
  }

  return [...errors, ...errorsToAdd];
};
